import { useState, useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, StopCircle, Wifi, WifiOff, Loader2, ScrollText } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { RunDetailSkeleton } from '@/components/skeletons/RunDetailSkeleton'
import { RunStatusBadge } from '@/components/runs/RunStatusBadge'
import { RunTimeline } from '@/components/runs/RunTimeline'
import { RunMetadata } from '@/components/runs/RunMetadata'
import { RunPullRequest } from '@/components/runs/RunPullRequest'
import { RunArtifacts } from '@/components/runs/RunArtifacts'
import { RunError } from '@/components/runs/RunError'
import { RunLogsDrawer } from '@/components/runs/RunLogsDrawer'
import { LogViewer } from '@/components/log-viewer/LogViewer'
import type { LogEntry } from '@/components/log-viewer/LogViewer'
import { ConstraintsInput } from '@/components/forms/ConstraintsInput'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useRun, useRunLogs, useCancelRun } from '@/api/runs'
import { useLogStream } from '@/hooks/useLogStream'
import type { RunStatus } from '@/lib/types'

/**
 * Parse raw logs string into LogEntry array
 */
function parseLogsToEntries(logsData: { logs?: string } | undefined): LogEntry[] {
  if (!logsData?.logs) return []
  
  const lines = logsData.logs.split('\n').filter(Boolean)
  return lines.map((line, index): LogEntry => {
    // Try to parse structured log format: [timestamp] [LEVEL] message
    const match = line.match(/^\[([^\]]+)\]\s*\[([^\]]+)\]\s*(.*)$/)
    if (match) {
      const level = match[2].toLowerCase()
      return {
        lineNumber: index + 1,
        timestamp: match[1],
        level: ['debug', 'info', 'warn', 'error'].includes(level) 
          ? level as LogEntry['level'] 
          : 'info',
        content: match[3],
      }
    }
    // Fallback for unstructured logs
    return {
      lineNumber: index + 1,
      content: line,
    }
  })
}

/**
 * Run detail page showing full run information.
 * Route: /#/runs/:id
 */
export function RunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showEventsDrawer, setShowEventsDrawer] = useState(false)

  const { data: run, isLoading: runLoading, error: runError } = useRun(id!)
  const cancelRun = useCancelRun()

  // Determine if run is active (streaming logs) or completed (static fetch)
  const isActive = run?.status && ['PENDING', 'QUEUED', 'RUNNING'].includes(run.status)
  
  // SSE streaming for active runs
  const {
    logs: streamLogs,
    connectionState,
    error: streamError,
  } = useLogStream(id!, {
    enabled: !!isActive,
  })

  // Static fetch for completed runs
  const { data: logsData, isLoading: logsLoading } = useRunLogs(id!, !isActive)

  // Choose which logs to display
  const logEntries = useMemo(() => {
    if (isActive) {
      // Use streamed logs for active runs
      return streamLogs
    }
    // Use static fetched logs for completed runs
    return parseLogsToEntries(logsData)
  }, [isActive, streamLogs, logsData])

  // Connection status indicator
  const ConnectionIndicator = () => {
    if (!isActive) return null
    
    switch (connectionState) {
      case 'connecting':
        return (
          <Badge variant="outline" className="gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Connecting...
          </Badge>
        )
      case 'connected':
        return (
          <Badge variant="outline" className="gap-1 text-green-600 border-green-200">
            <Wifi className="h-3 w-3" />
            Live
          </Badge>
        )
      case 'error':
        return (
          <Badge variant="destructive" className="gap-1">
            <WifiOff className="h-3 w-3" />
            Disconnected
          </Badge>
        )
      default:
        return null
    }
  }

  const handleCancel = async () => {
    if (!id) return
    
    try {
      await cancelRun.mutateAsync(id)
      setShowCancelDialog(false)
    } catch (error) {
      console.error('Failed to cancel run:', error)
    }
  }

  const canCancel = (status: RunStatus): boolean => {
    return ['PENDING', 'QUEUED', 'RUNNING'].includes(status)
  }

  const isCompleted = (status: RunStatus): boolean => {
    return ['SUCCEEDED', 'FAILED', 'TIMEOUT', 'CANCELLED'].includes(status)
  }

  if (runLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/runs">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runs
            </Link>
          </Button>
          <RunDetailSkeleton />
        </div>
      </PageShell>
    )
  }

  if (runError || !run) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/runs">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runs
            </Link>
          </Button>
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-destructive mb-2">
              Failed to load run
            </h2>
            <p className="text-muted-foreground mb-4">
              The run could not be found or there was an error loading it.
            </p>
            <Button onClick={() => navigate('/runs')}>
              Go to Runs
            </Button>
          </div>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Back Link */}
        <Button variant="ghost" asChild>
          <Link to="/runs">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Runs
          </Link>
        </Button>

        {/* Run Header — status indicator + metadata strip */}
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-primary/80 to-primary/20" />
          <CardContent className="py-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <RunStatusBadge status={run.status} size="md" />
                <h1 className="font-mono text-base font-semibold">{run.run_id}</h1>
              </div>
            </div>
            <RunTimeline
              status={run.status}
              timestamps={{
                created: run.timestamps?.created,
                started: run.timestamps?.started,
                finished: run.timestamps?.finished,
              }}
            />
          </CardContent>
        </Card>

        {/* Error Banner (if failed) */}
        {run.error_message && (
          <RunError error={run.error_message} />
        )}

        {/* Main Content - Two column layout with sticky sidebar */}
        <div className="flex gap-6">
          {/* Left Column: Task + Logs */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Task + Constraints */}
            <Card>
              <CardContent className="py-4">
                <h2 className="text-sm font-medium text-muted-foreground mb-2">Task</h2>
                <p className="text-base leading-relaxed whitespace-pre-wrap">{run.goal}</p>
                
                {/* Constraints using ConstraintsInput readOnly */}
                {run.constraints && run.constraints.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Constraints</h3>
                    <ConstraintsInput
                      value={run.constraints}
                      onChange={() => {}}
                      readOnly
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logs */}
            <Card>
              <CardContent className="py-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-muted-foreground">Logs</h3>
                  <ConnectionIndicator />
                </div>
                {streamError && (
                  <p className="text-sm text-destructive mb-2">
                    {streamError.message}
                  </p>
                )}
                <div className="-mx-6 -mb-3">
                  <LogViewer
                    logs={logEntries}
                    isLoading={runLoading || logsLoading}
                    autoScroll={isActive}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="w-72 shrink-0">
            <div className="sticky top-6 space-y-4">
              {/* Details */}
              <Card>
                <CardContent className="py-4">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Details</h3>
                  <RunMetadata run={run} />
                </CardContent>
              </Card>

              {/* Pull Request */}
              <Card>
                <CardContent className="py-4">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Pull Request</h3>
                  <RunPullRequest prUrl={run.pr_url} />
                </CardContent>
              </Card>

              {/* Actions */}
              <Card>
                <CardContent className="py-4 space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground">Actions</h3>
                  
                  {canCancel(run.status) && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowCancelDialog(true)}
                      disabled={cancelRun.isPending}
                      className="w-full"
                    >
                      <StopCircle className="h-4 w-4 mr-2" />
                      Cancel Run
                    </Button>
                  )}
                  
                  {isCompleted(run.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEventsDrawer(true)}
                      className="w-full"
                    >
                      <ScrollText className="h-4 w-4 mr-2" />
                      View Execution Events
                    </Button>
                  )}
                  
                  {/* Artifacts - show for both succeeded and failed runs */}
                  {(run.status === 'SUCCEEDED' || run.status === 'FAILED') && (
                    <div className="pt-2 border-t">
                      <h4 className="text-xs font-medium text-muted-foreground mb-2">Artifacts</h4>
                      <RunArtifacts artifacts={run.artifacts} />
                    </div>
                  )}
                  
                  {!canCancel(run.status) && !isCompleted(run.status) && (
                    <p className="text-sm text-muted-foreground">No actions available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title="Cancel Run?"
        description={`Are you sure you want to cancel ${run.run_id}? This action cannot be undone. The Kubernetes job will be terminated immediately.`}
        confirmLabel="Cancel Run"
        cancelLabel="Keep Running"
        onConfirm={handleCancel}
        variant="destructive"
        isLoading={cancelRun.isPending}
      />

      {/* Execution Events Drawer */}
      <RunLogsDrawer
        runId={run.run_id}
        open={showEventsDrawer}
        onOpenChange={setShowEventsDrawer}
      />
    </PageShell>
  )
}

export default RunDetailPage

